"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Card from "../../components/inventory/modules/Card/Card"
import Tabs from "../../components/inventory/modules/Tabs/Tabs"
import Select from "../../components/inventory/modules/Select/Select"
import OrderInventory from "../../components/inventory/order"
import DashboardStats from "../../components/inventory/sections/DashboardStats"
import DashboardMetricsCard from "../../components/inventory/sections/DashboardMetricsCard"
import PendingOrdersSection from "../../components/inventory/sections/PendingOrdersSection"
import DropshipRequestsSection from "../../components/inventory/sections/DropshipRequestsSection"
import Badge from "../../components/inventory/modules/Badge/Badge"
import styles from "./InventoryDashboardPage.module.css"
import { getLocationList } from "../../services/inventory/detail"
import { getWarehouseStats } from "../../services/inventory/devices"

const getActiveClientId = () => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null
    const storedAcc = localStorage.getItem("inventory_active_account")
    if (!storedAcc) return null
    const account = JSON.parse(storedAcc)

    if (account?.role === "Admin") {
      return null
    }
    return account?.id || null
  } catch (err) {
    return null
  }
}

const SubAccountDashboardView = ({ currentUser }) => {
  const navigate = useNavigate()
  const [activeMainTab, setActiveMainTab] = useState("refurbishment")
  const [activeSubTab, setActiveSubTab] = useState("refurbished-devices")
  const [selectedLocation, setSelectedLocation] = useState("all")
  const [locationOptions, setLocationOptions] = useState([{ value: "all", label: "All Locations" }])
  const [orderInventoryModal, setOrderInventoryModal] = useState({ isOpen: false })
  const [activeOrderTab, setActiveOrderTab] = useState("pending-orders")

  // KPI state & loading
  const [inventoryStats, setInventoryStats] = useState([
    { title: "Total Devices", value: "0", changeType: "neutral", iconColor: "#10164d" },
    { title: "Assigned Devices", value: "0", changeType: "positive", iconColor: "#3b82f6" },
    { title: "Available Devices", value: "0", changeType: "neutral", iconColor: "#06b6d4" },
    { title: "Unlinked Devices", value: "0", changeType: "positive", iconColor: "#ef4444" },
  ])
  const [kpiLoading, setKpiLoading] = useState(false)

  // deviceDistribution (for DashboardMetricsCard) + loading for metrics fetch
  const [deviceDistributionState, setDeviceDistributionState] = useState([
    { name: "Kalioundji", value: 35, color: "#10B981" },
    { name: "Fakhrei", value: 25, color: "#3B82F6" },
    { name: "RCMG", value: 20, color: "#F59E0B" },
    { name: "Others", value: 20, color: "#8B5CF6" },
  ])
  const [metricsLoading, setMetricsLoading] = useState(false)

  // ✅ Fetch dynamic locations using getLocationList()
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await getLocationList()
        if (response?.success && Array.isArray(response.data)) {
          const formattedLocations = response.data.map((loc) => ({
            // 👇 use location name as value for easy filtering
            value: loc.name?.trim() || "Unnamed Location",
            label: loc.name || "Unnamed Location",
          }))
          setLocationOptions([{ value: "all", label: "All Locations" }, ...formattedLocations])
        }
      } catch (err) {
        console.error("Error fetching locations:", err)
      }
    }
    fetchLocations()
  }, [])

  // fetch warehouse KPIs and set inventoryStats accordingly
  useEffect(() => {
    let mounted = true
    const fetchKpis = async () => {
      setKpiLoading(true)
      try {
        const statsResponse = await getWarehouseStats()
        // new possible shapes:
        // 1) { tenants_stats: [...], warehouse_stats: [...], total_Warehouse_devices: N }
        // 2) array of tenant objects (legacy)
        // 3) { data: [...] }
        const tenants =
          Array.isArray(statsResponse)
            ? statsResponse
            : Array.isArray(statsResponse?.tenants_stats)
            ? statsResponse.tenants_stats
            : Array.isArray(statsResponse?.data)
            ? statsResponse.data
            : []

        // Determine whether the active account is admin or a sub-tenant
        const activeClientId = getActiveClientId()
        const isAdmin = activeClientId === null

        if (isAdmin) {
          // Sum across tenants (safe numeric conversions)
          const totals = tenants.reduce(
            (acc, t) => {
              const devices = t?.devices || {}
              acc.total_devices += Number(devices.total_devices ?? 0)
              acc.assigned += Number(devices.assigned ?? 0)
              acc.available += Number(devices.available ?? 0)
              acc.unlinked += Number(devices.unlinked ?? 0)
              return acc
            },
            { total_devices: 0, assigned: 0, available: 0, unlinked: 0 },
          )

          if (!mounted) return
          setInventoryStats([
            { title: "Total Devices", value: String(totals.total_devices), changeType: "neutral", iconColor: "#10164d" },
            { title: "Assigned Devices", value: String(totals.assigned), changeType: "positive", iconColor: "#3b82f6" },
            { title: "Available Devices", value: String(totals.available), changeType: "neutral", iconColor: "#06b6d4" },
            { title: "Unlinked Devices", value: String(totals.unlinked), changeType: "positive", iconColor: "#ef4444" },
          ])
        } else {
          // Non-admin: find matching tenant by tenant_id or tenant
          const tenant = tenants.find((t) => {
            if (!t) return false
            return String(t.tenant_id || t.tenant || "").toLowerCase() === String(activeClientId || "").toLowerCase()
          })

          if (tenant) {
            const devices = tenant.devices || {}
            if (!mounted) return
            setInventoryStats([
              { title: "Total Devices", value: String(devices.total_devices ?? 0), changeType: "neutral", iconColor: "#10164d" },
              { title: "Assigned Devices", value: String(devices.assigned ?? 0), changeType: "positive", iconColor: "#3b82f6" },
              { title: "Available Devices", value: String(devices.available ?? 0), changeType: "neutral", iconColor: "#06b6d4" },
              { title: "Unlinked Devices", value: String(devices.unlinked ?? 0), changeType: "positive", iconColor: "#ef4444" },
            ])
          } else {
            // Tenant not found — show zeros (explicit)
            if (!mounted) return
            setInventoryStats([
              { title: "Total Devices", value: "0", changeType: "neutral", iconColor: "#10164d" },
              { title: "Assigned Devices", value: "0", changeType: "positive", iconColor: "#3b82f6" },
              { title: "Available Devices", value: "0", changeType: "neutral", iconColor: "#06b6d4" },
              { title: "Unlinked Devices", value: "0", changeType: "positive", iconColor: "#ef4444" },
            ])
          }
        }
      } catch (err) {
        console.error("Error fetching warehouse KPIs:", err)
        // leave inventoryStats as-is (previous defaults) — safer fallback
      } finally {
        if (mounted) setKpiLoading(false)
      }
    }

    fetchKpis()
    return () => {
      mounted = false
    }
  }, [])

  // Fetch warehouse stats to build deviceDistribution for DashboardMetricsCard (same logic as admin)
  useEffect(() => {
    let mounted = true
    const loadStats = async () => {
      setMetricsLoading(true)
      try {
        const res = await getWarehouseStats()
        // prefer tenants_stats, fallback to array shape or data
        const tenants =
          Array.isArray(res)
            ? res
            : Array.isArray(res?.tenants_stats)
            ? res.tenants_stats
            : Array.isArray(res?.data)
            ? res.data
            : []

        if (!Array.isArray(tenants) || tenants.length === 0) {
          if (mounted) setMetricsLoading(false)
          return
        }

        const mapped = tenants.map((t, idx) => {
          const name = t?.tenant_name || t?.tenant || `Tenant ${idx + 1}`
          const value = Number(t?.devices?.total_devices || 0)
          return { name, value }
        })

        if (mounted && Array.isArray(mapped) && mapped.length > 0) {
          setDeviceDistributionState(mapped)
        }
      } catch (err) {
        console.error("Failed to load warehouse stats for deviceDistribution:", err)
        // keep default distribution
      } finally {
        if (mounted) setMetricsLoading(false)
      }
    }

    loadStats()
    return () => {
      mounted = false
    }
  }, [])

  // Account Information Data
  const accountInfo = {
    accountName: currentUser.name,
    phone: currentUser.phone_number || "N/A",
    location: "New Hall",
    address: currentUser.address_line_1 || "N/A",
    otherContact: currentUser.email || "N/A",
  }

  // Chart data (dummy) — plug deviceDistributionState into chartData
  const chartData = {
    refurbishedDevices: [
      { name: "TELE RPM", value: 45, color: "#10B981" },
      { name: "Tenovi", value: 35, color: "#3B82F6" },
      { name: "Other Devices", value: 20, color: "#F59E0B" },
    ],
    refurbishmentStatus: [
      { name: "Ready", value: 60, color: "#10B981" },
      { name: "Pending", value: 25, color: "#F59E0B" },
      { name: "Needs Warranty", value: 15, color: "#EF4444" },
    ],
    warehouseInventory: [
      { name: "Distributed", value: 14, color: "#87CEEB" },
      { name: "Yet to Receive", value: 30, color: "#F59E0B" },
      { name: "Warehouse", value: 30, color: "#10B981" },
      { name: "Refurbished", value: 26, color: "#8B5CF6" },
    ],
    deviceDistribution: deviceDistributionState,
  }

  // 🔹 Dummy orders data
  const allOrdersData = [
    { id: 1, orderNumber: "ORD-2023-001", location: "Dr. Masoud Azizad", orderDate: "3-14-2023", devices: "40", status: "In Transit" },
    { id: 2, orderNumber: "ORD-2023-002", location: "Chicago", orderDate: "3-14-2023", devices: "25", status: "In Transit" },
    { id: 3, orderNumber: "ORD-2023-003", location: "Miami", orderDate: "3-14-2023", devices: "3", status: "Done" },
  ]

  // ✅ Apply location filter to pending orders
  const pendingOrdersData =
    selectedLocation === "all"
      ? allOrdersData
      : allOrdersData.filter((order) => order.location?.trim().toLowerCase() === selectedLocation.trim().toLowerCase())

  const pendingOrdersColumns = [
    { key: "orderNumber", label: "Order #", alignLeft: true },
    { key: "location", label: "Location", alignLeft: true },
    { key: "orderDate", label: "Order Date", alignLeft: true, sorting: false },
    { key: "devices", label: "Devices", alignLeft: true },
    {
      key: "status",
      label: "Status",
      sorting: false,
      render: (row) => (
        <Badge
          variant={
            row.status === "Done"
              ? "success"
              : row.status === "In Transit"
              ? "warning"
              : row.status === "pending"
              ? "warning"
              : "default"
          }
        >
          {row.status}
        </Badge>
      ),
    },
  ]

  const mainTabs = [
    { id: "inventory", label: "Warehouse Overview" },
    { id: "refurbishment", label: "Refurbishment" },
    { id: "warranty", label: "Warranty & Purchases" },
    { id: "product", label: "Product & Claims" },
  ]

  const subTabs = {
    refurbishment: [
      { id: "refurbished-devices", label: "Refurbished Devices" },
      { id: "refurbishment-status", label: "Refurbishment Status" },
    ],
    inventory: [
      { id: "warehouse-inventory", label: "Warehouse Inventory" },
      { id: "device-distribution", label: "Device Distribution" },
    ],
    warranty: [
      { id: "device-purchase-warranty", label: "Device Purchase/Warranty Overview" },
      { id: "warranties-products", label: "Warranties on Products" },
    ],
    product: [
      { id: "product-type", label: "Product Type" },
      { id: "claims-summary", label: "Claims Summary" },
    ],
  }

  const handleViewFullInventory = () => navigate("/inventory/supplies")
  const handleOrderDevices = () => setOrderInventoryModal({ isOpen: true })
  const handleLocationChange = (value) => setSelectedLocation(value)

  const orderSectionTabs = [
    { id: "pending-orders", label: "My Pending Orders" },
    { id: "dropship-requests", label: "Dropship Requests" },
  ]

  return (
    <>
      {/* Account Information Section */}
      <div className={styles.section}>
        <Card title="Account Information" className={styles.account_card}>
          <div className={styles.account_grid}>
            <div className={styles.account_item}>
              <label>Account Name</label>
              <span>{accountInfo.accountName}</span>
            </div>
            <div className={styles.account_item}>
              <label>Phone</label>
              <span>{accountInfo.phone}</span>
            </div>
            <div className={styles.account_item}>
              <label>Other Contact</label>
              <span>{accountInfo.otherContact}</span>
            </div>
            <div className={styles.account_item}>
              <label>Location</label>
              <span>{accountInfo.location}</span>
            </div>
            <div className={styles.account_item}>
              <label>Address</label>
              <span>{accountInfo.address}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Dashboard KPIs (now dynamic) */}
      <div className={styles.section}>
        {/* DashboardStats expects the same shape so we pass inventoryStats.
            When kpiLoading is true, DashboardStats can show a loader internally;
            if it doesn't, you can render a placeholder here. */}
        <DashboardStats stats={inventoryStats} loading={kpiLoading} />
      </div>

      <DashboardMetricsCard
        chartData={chartData}
        headerButtons={[
          { label: "View Full Inventory", variant: "secondary", onClick: handleViewFullInventory },
          { label: "Order Devices", variant: "primary", onClick: handleOrderDevices },
        ]}
        mainTabs={mainTabs}
        subTabs={subTabs}
        loading={metricsLoading}
      />

      <div className={styles.section}>
        <Card title={"Pending Orders"}>
          <div className={styles.tabs_container}>
            <Tabs
              tabs={orderSectionTabs}
              activeTab={activeOrderTab}
              onChange={setActiveOrderTab}
              variant="secondary"
              size="medium"
            />
          </div>

          {activeOrderTab === "pending-orders" && (
            <PendingOrdersSection
              title={`My Pending Orders ${selectedLocation !== "all" ? `(${selectedLocation})` : ""}`}
              data={pendingOrdersData}
              columns={pendingOrdersColumns}
            />
          )}

          {activeOrderTab === "dropship-requests" && <DropshipRequestsSection selectedLocation={selectedLocation} />}
        </Card>
      </div>

      {/* Order Inventory Modal */}
      {orderInventoryModal.isOpen && (
        <OrderInventory isOpen={orderInventoryModal.isOpen} setIsOpen={(isOpen) => setOrderInventoryModal({ isOpen })} />
      )}
    </>
  )
}

export default SubAccountDashboardView
